import { useEffect, useRef, useState } from 'react';
import motoPadrao from '../../../assets/app-locatario/moto-padrao.webp';
import carroPadrao from '../../../assets/app-locatario/carro-padrao.webp';
import { App, Moldura, Tela } from './styles';

export type TelaApp = 'inicio' | 'pagamentos' | 'oleo' | 'vistoria' | 'documentos';

export interface PagamentoApp {
    id: string;
    valor: number;
    descricao: string;
    data: string;
    status: 'pago' | 'aberto' | 'atraso';
}

export interface DadosApp {
    primeiroNome: string;
    nomeLocatario: string;
    documento: string;
    nomeLocadora: string;
    placa: string;
    marca: string;
    modelo: string;
    ano: string;
    cor: string;
    carro: boolean;
    imagem?: string;
    valorAluguel: number;
    periodicidade: string;
    pagamentos: PagamentoApp[];
    linkContrato?: string;
}

interface Props {
    dados: DadosApp;
    tela: TelaApp;
    escala: number;
    destaque?: 'pix' | 'upload' | null;
    aoMudarTela: (tela: TelaApp) => void;
    aoTocarPix?: () => void;
    aoTocarUpload?: () => void;
}

const dinheiro = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const horaAgora = () => new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

function IconesStatus({ cor }: { cor: string }) {
    return (
        <svg width="86" height="15" viewBox="0 0 86 15">
            <g fill={cor}>
                <rect x="19" y="9" width="2.4" height="5" rx="1" /><rect x="23" y="6.5" width="2.4" height="7.5" rx="1" />
                <rect x="27" y="4" width="2.4" height="10" rx="1" />
                <rect x="34" y="9" width="2.4" height="5" rx="1" /><rect x="38" y="6.5" width="2.4" height="7.5" rx="1" />
                <rect x="42" y="4" width="2.4" height="10" rx="1" />
                <path d="M55 13.5l-5-6a7.6 7.6 0 0 1 10 0z" />
                <rect x="64" y="4" width="16" height="9" rx="2.4" fill="none" stroke={cor} strokeWidth="1.4" />
                <rect x="80.5" y="6.5" width="1.8" height="4" rx=".8" />
            </g>
            <text x="67" y="11.5" fontFamily="Roboto, Arial" fontSize="7.5" fill={cor}>8</text>
        </svg>
    );
}

export function BarraStatus({ clara }: { clara?: boolean }) {
    return (
        <div className={`status${clara ? ' clara' : ''}`}>
            <span>{horaAgora()}</span>
            <IconesStatus cor={clara ? '#fff' : '#2b3038'} />
        </div>
    );
}

const ABAS: { tela: TelaApp; rotulo: string; icone: (ativa: boolean) => JSX.Element }[] = [
    {
        tela: 'inicio', rotulo: 'Dashboard',
        icone: ativa => ativa
            ? <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3l9 8h-2.6v9h-5.2v-6h-2.4v6H5.6v-9H3z" /></svg>
            : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 11l9-8 9 8" /><path d="M5.8 9.6V20h4.4v-6h3.6v6h4.4V9.6" /></svg>,
    },
    {
        tela: 'oleo', rotulo: 'Óleo',
        icone: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.7 3.3a5.4 5.4 0 0 0-7 6.9L4.4 17.5a2.1 2.1 0 1 0 3 3l7.3-7.3a5.4 5.4 0 0 0 6.9-7l-3 3-2.5-.7-.7-2.5 3-3zM6 19.6a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2z" /></svg>,
    },
    {
        tela: 'vistoria', rotulo: 'Vistoria',
        icone: ativa => ativa
            ? <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M9 4l-1.5 2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-2.5L15 4z" /><circle cx="12" cy="13" r="3.6" fill="#fff" /></svg>
            : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 8a2 2 0 0 1 2-2h2.5L9 4h6l1.5 2H19a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><circle cx="12" cy="13" r="3.6" /></svg>,
    },
    {
        tela: 'documentos', rotulo: 'Docs',
        icone: ativa => ativa
            ? <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7zm0 2l5 5h-5z" /><path d="M9 13h6M9 17h6" stroke="#fff" strokeWidth="1.8" /></svg>
            : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" /><path d="M14 2v5h5M9 13h6M9 17h6" /></svg>,
    },
];

function Abas({ ativa, aoMudarTela }: { ativa: TelaApp; aoMudarTela: (tela: TelaApp) => void }) {
    return (
        <nav className="abas">
            {ABAS.map(a => (
                <button type="button" key={a.tela} className={`aba${a.tela === ativa ? ' ativa' : ''}`} onClick={() => aoMudarTela(a.tela)}>
                    {a.icone(a.tela === ativa)}{a.rotulo}
                </button>
            ))}
        </nav>
    );
}

function TelaInicio({ dados, aoMudarTela }: { dados: DadosApp; aoMudarTela: (tela: TelaApp) => void }) {
    return (
        <section className="tela">
            <BarraStatus />
            <div className="header-claro">
                <h1>Olá, {dados.primeiroNome}</h1>
                <div className="acoes">
                    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.7"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
                    <span className="btn-sair">
                        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.7"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></svg>
                        Sair
                    </span>
                </div>
            </div>

            <div className="conteudo">
                <div className="cartao">
                    <div className="card-titulo">
                        {dados.carro
                            ? <svg width="26" height="26" viewBox="0 0 24 24" fill="var(--primary)"><path d="M5 11l1.6-4.6A2 2 0 0 1 8.5 5h7a2 2 0 0 1 1.9 1.4L19 11a2 2 0 0 1 2 2v4a1 1 0 0 1-1 1h-1a2 2 0 0 1-4 0H9a2 2 0 0 1-4 0H4a1 1 0 0 1-1-1v-4a2 2 0 0 1 2-2zm2.1 0h9.8l-1.2-3.6a.6.6 0 0 0-.6-.4H8.9a.6.6 0 0 0-.6.4zM6.5 15.5a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4zm11 0a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4z" /></svg>
                            : <svg width="26" height="26" viewBox="0 0 24 24" fill="var(--primary)"><path d="M4.9 11.5a4.25 4.25 0 1 0 0 8.5 4.25 4.25 0 0 0 0-8.5zm0 6.2a1.95 1.95 0 1 1 0-3.9 1.95 1.95 0 0 1 0 3.9z" /><path d="M19.1 11.5a4.25 4.25 0 1 0 0 8.5 4.25 4.25 0 0 0 0-8.5zm0 6.2a1.95 1.95 0 1 1 0-3.9 1.95 1.95 0 0 1 0 3.9z" /><rect x="1.9" y="8.5" width="6.6" height="1.95" rx=".97" /><rect x="13.9" y="6.1" width="6.9" height="1.95" rx=".97" /><path d="M6.3 10.4h7.5l2.7 4.8-3.6 1.5-5.4-1.5-2.5-2.7z" /><path d="M15.2 7.7l2 3.6-1.7 1-2.2-3.8z" /></svg>}
                        Veículo Locado
                    </div>
                    <div className="veiculo"><img src={dados.imagem || (dados.carro ? carroPadrao : motoPadrao)} alt="Veículo locado" /></div>
                    <div className="grade">
                        <div><div className="rotulo">Placa</div><div className="valor">{dados.placa}</div></div>
                        <div><div className="rotulo">Marca</div><div className="valor">{dados.marca}</div></div>
                        <div><div className="rotulo">Modelo</div><div className="valor">{dados.modelo}</div></div>
                        <div><div className="rotulo">Ano</div><div className="valor">{dados.ano || '-'}</div></div>
                    </div>
                </div>

                <div className="cartao clicavel" onClick={() => aoMudarTela('pagamentos')}>
                    <div className="card-titulo">
                        <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.8"><rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M2 10h20" /></svg>
                        Informações de Pagamento
                    </div>
                    <div className="rotulo-valor">Valor do Aluguel</div>
                    <div className="valor-grande">{dinheiro(dados.valorAluguel)}</div>
                    <div className="chip">{dados.periodicidade}</div>
                    <button type="button" className="botao">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l3 2" /></svg>
                        Histórico de Pagamentos
                    </button>
                </div>
            </div>

            <Abas ativa="inicio" aoMudarTela={aoMudarTela} />
        </section>
    );
}

function TelaPagamentos({ dados, destacarPix, aoMudarTela, aoTocarPix }: { dados: DadosApp; destacarPix: boolean; aoMudarTela: (tela: TelaApp) => void; aoTocarPix: () => void }) {
    const primeiroEmAberto = dados.pagamentos.find(p => p.status !== 'pago')?.id;
    return (
        <>
            <TelaInicio dados={dados} aoMudarTela={aoMudarTela} />
            <div className="veu" onClick={() => aoMudarTela('inicio')} />
            <div className="janela">
                <div className="modal-topo">
                    <div>
                        <h2>Histórico de Pagamentos</h2>
                        <div className="sub">{dados.modelo} ({dados.placa})</div>
                    </div>
                    <div className="acoes">
                        <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></svg>
                        <button type="button" aria-label="Fechar" onClick={() => aoMudarTela('inicio')}>
                            <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
                        </button>
                    </div>
                </div>
                <div className="lista">
                    {dados.pagamentos.length === 0 && <p className="vazio">Nenhum pagamento por enquanto.</p>}
                    {dados.pagamentos.map(p => (
                        <div className="pagamento" key={p.id}>
                            <div className="linha">
                                <span className="preco">{dinheiro(p.valor)}</span>
                                <span className={`selo ${p.status}`}>{p.status === 'pago' ? 'PAGO' : p.status === 'atraso' ? 'EM ATRASO' : 'EM ABERTO'}</span>
                            </div>
                            <div className="tipo">{p.descricao}</div>
                            <div className="data">{p.status === 'pago' ? 'Pago em' : 'Vencimento'}: {p.data}</div>
                            {p.status !== 'pago' && (
                                <button type="button" className={`botao${destacarPix && p.id === primeiroEmAberto ? ' destaque' : ''}`} onClick={aoTocarPix}>
                                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                                    Pagar com Pix
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

function TelaPassoAPasso({ dados, tipo, destacarUpload, aoMudarTela, aoTocarUpload }: { dados: DadosApp; tipo: 'oleo' | 'vistoria'; destacarUpload: boolean; aoMudarTela: (tela: TelaApp) => void; aoTocarUpload: () => void }) {
    const oleo = tipo === 'oleo';
    return (
        <section className="tela">
            <BarraStatus clara />
            <div className="header-cor">{oleo ? 'Troca de óleo' : 'Vistoria'} - ({dados.placa})</div>
            <div className="etapa"><span className="texto">Etapa 1 de {oleo ? 5 : 8}</span><span className="pct">{oleo ? '20%' : '13%'}</span></div>
            <div className="barra"><i style={{ width: oleo ? '20%' : '13%' }} /></div>
            <div className="conteudo" style={{ paddingTop: 16 }}>
                <div className="cartao">
                    <div className="card-titulo">
                        {oleo
                            ? <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.8"><rect x="2" y="6" width="13" height="12" rx="2" /><path d="M15 10l7-3v10l-7-3z" /></svg>
                            : <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.8"><path d="M3 8a2 2 0 0 1 2-2h2.5L9 4h6l1.5 2H19a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><circle cx="12" cy="13" r="3.6" /></svg>}
                        {oleo ? 'Vídeo da Troca' : 'Foto Frontal'} <span className="obrigatorio">*</span>
                    </div>
                    <p className="descricao">
                        {oleo ? 'Grave um vídeo de 5 a 10 segundos.' : dados.carro ? 'Fotografe a frente do carro mostrando os faróis.' : 'Fotografe a frente da moto mostrando o farol.'}
                    </p>
                    <button type="button" className={`area-upload${destacarUpload ? ' destaque' : ''}`} onClick={aoTocarUpload}>
                        {oleo
                            ? <svg width="38" height="29" viewBox="0 0 34 26"><rect x="1" y="4" width="20" height="18" rx="3" fill="var(--primary)" /><path d="M21 10l11-5v16l-11-5z" fill="var(--primary)" /><path d="M11 9v8M7 13h8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" /></svg>
                            : <svg width="38" height="31" viewBox="0 0 34 28"><rect x="6" y="6" width="26" height="20" rx="3.5" fill="var(--primary)" /><circle cx="19" cy="16" r="6" fill="#fff" /><circle cx="19" cy="16" r="3.4" fill="var(--primary)" /><path d="M6 4v6M3 7h6" stroke="var(--primary)" strokeWidth="2.2" strokeLinecap="round" /></svg>}
                        <span>{oleo ? 'Adicionar Vídeo' : 'Adicionar Foto'}</span>
                    </button>
                    <button type="button" className="botao compacto" onClick={aoTocarUpload}>Próximo</button>
                </div>
            </div>
            <Abas ativa={tipo} aoMudarTela={aoMudarTela} />
        </section>
    );
}

function TelaDocumentos({ dados, aoMudarTela }: { dados: DadosApp; aoMudarTela: (tela: TelaApp) => void }) {
    return (
        <section className="tela">
            <BarraStatus clara />
            <div className="header-cor">Documentos</div>
            <p className="intro">Visualize seu contrato e o CRLV do veículo selecionado.</p>
            <div className="conteudo">
                <div className="cartao">
                    <div className="card-titulo" style={{ fontSize: 16, fontWeight: 700 }}>Contrato de Locação</div>
                    <div className="visor">
                        <div className="folha">
                            <h4>CONTRATO DE LOCAÇÃO</h4>
                            <p><b>LOCATÁRIO: {dados.nomeLocatario}</b>, inscrito(a) no CPF N° {dados.documento}.</p>
                            <p><b>LOCADOR: {dados.nomeLocadora}</b>.</p>
                            <p><b>As partes acima identificadas têm, entre si, justo e acertado o presente CONTRATO DE LOCAÇÃO, que será regido pelas cláusulas e condições abaixo dispostas.</b></p>
                            <p><b>CLÁUSULA 1ª – DO OBJETO DO CONTRATO</b></p>
                            <p>1.1 O objeto do presente contrato é o seguinte veículo automotor:<br />
                                <b>Placa:</b> {dados.placa}<br /><b>Tipo:</b> {dados.carro ? 'Carro' : 'Moto'}<br /><b>Marca:</b> {dados.marca}<br />
                                <b>Modelo:</b> {dados.modelo}<br />
                                {dados.cor && <><b>Cor:</b> {dados.cor}<br /></>}
                                {dados.ano && <><b>Ano:</b> {dados.ano}</>}</p>
                            <p>1.2 O veículo, objeto deste contrato, será utilizado <b>exclusivamente pelo LOCATÁRIO</b>, não sendo permitido sub-rogar os direitos por ele obtidos através do presente contrato a terceiros.</p>
                            <p><b>CLÁUSULA 2ª – DO VALOR</b></p>
                            <p>2.1 O LOCATÁRIO pagará ao LOCADOR o valor de <b>{dinheiro(dados.valorAluguel)}</b>, com periodicidade <b>{dados.periodicidade.toLowerCase()}</b>.</p>
                        </div>
                        <div className="controles">
                            <span>Página <span className="pag">1</span> / 10</span>
                            <span>−</span>
                            <span>+</span>
                        </div>
                    </div>
                    <button type="button" className="botao" onClick={() => dados.linkContrato && window.open(dados.linkContrato, '_blank', 'noopener,noreferrer')}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 8v7M9 12l3 3 3-3" /></svg>
                        Baixar Documento
                    </button>
                </div>
                <div className="cartao">
                    <div className="card-titulo" style={{ fontSize: 16, fontWeight: 700, marginBottom: 0, lineHeight: 1.3, display: 'block' }}>CRLV - {dados.modelo} ({dados.placa})</div>
                </div>
            </div>
            <Abas ativa="documentos" aoMudarTela={aoMudarTela} />
        </section>
    );
}

/** Escala dos celulares das etapas dos apps (locatário e locador), pela altura da janela. */
export function calcularEscalaCelular() {
    const disponivel = window.innerHeight - 400;
    return Math.min(0.6, Math.max(0.5, disponivel / 800));
}

export default function CelularLocatario({ dados, tela, escala, destaque, aoMudarTela, aoTocarPix, aoTocarUpload }: Props) {
    const [aviso, setAviso] = useState('');
    const temporizador = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => { if (temporizador.current) clearTimeout(temporizador.current); }, []);

    const mostrarAviso = (texto: string) => {
        setAviso(texto);
        if (temporizador.current) clearTimeout(temporizador.current);
        temporizador.current = setTimeout(() => setAviso(''), 2200);
    };

    const tocarPix = () => { mostrarAviso('Código PIX copiado!'); aoTocarPix?.(); };
    const tocarUpload = () => { mostrarAviso(tela === 'oleo' ? 'Abrindo a câmera para gravar...' : 'Abrindo a câmera...'); aoTocarUpload?.(); };

    return (
        <Moldura $escala={escala}>
            <Tela $escala={escala}>
                <App $escala={escala}>
                    {tela === 'inicio' && <TelaInicio dados={dados} aoMudarTela={aoMudarTela} />}
                    {tela === 'pagamentos' && <TelaPagamentos dados={dados} destacarPix={destaque === 'pix'} aoMudarTela={aoMudarTela} aoTocarPix={tocarPix} />}
                    {(tela === 'oleo' || tela === 'vistoria') && <TelaPassoAPasso dados={dados} tipo={tela} destacarUpload={destaque === 'upload'} aoMudarTela={aoMudarTela} aoTocarUpload={tocarUpload} />}
                    {tela === 'documentos' && <TelaDocumentos dados={dados} aoMudarTela={aoMudarTela} />}
                    {aviso && <div className="aviso-toque">{aviso}</div>}
                    <div className="barra-gestos" />
                </App>
            </Tela>
        </Moldura>
    );
}
